/**
 * Workflows P5-8 (DEC-098): the `attribute_changed` and `visitor_clicks_element` triggers and the actor of a status change.
 * Mirrors backend `app/core/constants/workflow_triggers.py`.
 */
import type { AttributeObject } from "./workflows";
import { CUSTOMER_FIELD_CHOICES } from "./workflows-customer";
import { SOURCE_NOTICES, SOURCE_RUN_KINDS } from "./workflows-p6";

/** `trigger_options.frequency` of a click workflow: every click (at most one per 30 seconds), once per visitor, or once per tab session. */
export const CLICK_FREQUENCIES = ["every_click", "once", "every_session"] as const;
export type ClickFrequency = (typeof CLICK_FREQUENCIES)[number];
export const CLICK_DEFAULT_FREQUENCY: ClickFrequency = "every_click";

/** The selector grammar's limits (backend `SELECTOR_*`); the stored text may run to `field`, the grammar itself to `chars`. */
export const SELECTOR_LIMITS = { chars: 200, field: 1000, selectors: 5, tag: 32, ident: 64, value: 100 } as const;

/** A click is reported at most once per workflow every 30 seconds (the loader and the server both check). */
export const CLICK_DEBOUNCE_MS = 30_000;
/** Click workflows the public widget config lists. */
export const CLICK_TRIGGERS_MAX = 20;

/** Triggers that only run in the website widget (the picker says so; the channels are fixed to it). */
export const WIDGET_ONLY_TRIGGERS: ReadonlySet<string> = new Set(["visitor_clicks_element"]);
export const WIDGET_CHANNEL = "widget";

/** Who made a change (`event.changed_by`): a status, an assignment, a lead edit or a booking. `visitor` is a calendar-mode booking. */
export const CHANGED_BY_VALUES = ["staff", "assistant", "system", "visitor"] as const;
export type ChangedBy = (typeof CHANGED_BY_VALUES)[number];

/** The attribute an `attribute_changed` workflow watches (`settings.trigger_options.attribute`). */
export interface WatchedAttribute {
  object: AttributeObject;
  key: string;
}

/** Condition fields only one trigger can fill: the field picker lists them for that trigger only (backend `CONDITION_FIELDS`). */
export const TRIGGER_ONLY_FIELDS: Readonly<Record<string, readonly string[]>> = {
  "event.changed_by": ["conversation_status_changed", "conversation_assigned", "lead_updated", "booking_confirmed"],
  "event.attribute": ["attribute_changed"],
  "event.previous": ["attribute_changed"],
  "event.value": ["attribute_changed"],
  "event.actor_user_id": ["conversation_assigned", "lead_updated", "gap_assigned", "ticket_created", "ticket_updated", "ticket_state_changed", "ticket_assigned", "ticket_customer_replied", "ticket_staff_replied", "ticket_note_added", "ticket_resolved", "ticket_reopened"],
  "event.changed": ["lead_updated"],
  "source.notice": ["source_processed", "source_failed"],
  "source.run_kind": ["source_processed", "source_failed"],
  "connector.event": ["connector_event_received"],
  "connector.id": ["connector_event_received"],
  "connector.hook": ["connector_event_received"],
  "office.open": ["handoff_requested", "handoff_reminder_due", "handoff_escalation_due", "handoff_waiting_digest_due"],
  // Orders and quotes (M27 S2, scope 14.2): the facts of the order or quote the run is about
  "order.status": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "order.total_php": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "order.approval_state": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "order.kind": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "order.connector_id": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "order.line_count": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "order.has_backorder": ["order_placed", "order_status_changed", "order_approval_requested", "order_approval_decided", "order_sync_failed"],
  "quote.status": ["quote_status_changed"],
  "quote.source": ["quote_status_changed"],
  "quote.total_php": ["quote_status_changed"],
  "quote.line_count": ["quote_status_changed"],
  "quote.expired": ["quote_status_changed"],
  "event.final": ["order_approval_decided"],
  "event.side": ["order_approval_requested", "order_approval_decided"],
};

/** Condition fields with a short list of values: the editor offers them as choices. */
export const FIELD_CHOICES: Readonly<Record<string, readonly string[]>> = {
  "event.changed_by": CHANGED_BY_VALUES,
  "source.notice": SOURCE_NOTICES,
  "source.run_kind": SOURCE_RUN_KINDS,
  ...CUSTOMER_FIELD_CHOICES,
};
